/**
 * Pure helpers for rendering Mermaid fences (```mermaid) in chat markdown.
 * The React side lives in MermaidDiagram.tsx; everything here is testable
 * without a DOM.
 */
import { fnv1a32 } from "../../lib/diffRendering";
import { LRUCache } from "../../lib/lruCache";

export type MermaidTheme = "light" | "dark";

/** Fence languages rendered as a Mermaid diagram instead of source code. */
const MERMAID_FENCE_LANGUAGES: ReadonlySet<string> = new Set(["mermaid", "mmd"]);

export function isMermaidFenceLanguage(
  language: string,
  stream?: { readonly isStreaming: boolean; readonly code: string },
): boolean {
  const normalized = language.trim().toLowerCase();
  if (MERMAID_FENCE_LANGUAGES.has(normalized)) return true;
  // A chunk can end inside the opening language tag (e.g. ```merm).
  // Reserve the figure placeholder until the tag/body arrives; a completed
  // message or a fence with actual code must retain its ordinary rendering.
  if (stream?.isStreaming && stream.code.trim() === "" && normalized !== "") {
    for (const candidate of MERMAID_FENCE_LANGUAGES) {
      if (candidate.startsWith(normalized)) return true;
    }
  }
  return false;
}

/**
 * Mermaid's theme is global config, but an `%%{init}%%` directive at the top
 * of a diagram applies per render. Prepending one keeps light/dark rendering
 * correct without re-initialising the library on every theme switch; a
 * directive the author wrote later in the source still wins.
 */
export function buildMermaidSource(code: string, theme: MermaidTheme): string {
  // Only the theme name goes in the directive. Overriding `themeVariables`
  // (even one nested key) replaces mermaid's derived colour set for that
  // diagram type and washes out text and axes; surface tweaks live in CSS.
  const mermaidTheme = theme === "dark" ? "dark" : "neutral";
  const directive = `%%{init: {"theme": "${mermaidTheme}"}}%%`;
  // Labels must stay SVG text: the rendered markup is sanitised with the SVG
  // profile, which drops the foreignObject HTML labels produce. An author's
  // directive or front matter switching htmlLabels back on would render
  // blank labels, so that switch is turned off in the source itself.
  const trimmed = code.trim().replace(/(htmlLabels(?:["']?)\s*:\s*)true\b/gi, "$1false");
  // A YAML front matter block (mermaid's config syntax) must stay the very
  // first thing in the source; a directive above it is a parse error.
  const frontMatter = trimmed.match(/^---\r?\n[\s\S]*?\r?\n---(?:\r?\n|$)/);
  if (frontMatter) {
    const body = trimmed.slice(frontMatter[0].length).trim();
    return `${frontMatter[0].trimEnd()}\n${directive}\n${body}`;
  }
  return `${directive}\n${trimmed}`;
}

/**
 * While a message streams, the fence body arrives token by token. A body
 * with no line break yet is at most the diagram-type header (e.g. `xychart-beta`
 * or `flowchart TD`) — it cannot render, so skip the parse round trip.
 */
export function isRenderableMermaidCandidate(code: string): boolean {
  const trimmed = code.trim();
  // `flowchart TD; A --> B` is a complete one-line diagram.
  return trimmed.length > 0 && (trimmed.includes("\n") || trimmed.includes(";"));
}

/**
 * Two independent 32-bit hashes plus the length: a collision needs equal
 * length and both hashes equal, which keeps the key small (the cache is
 * memory-bounded) without making one message's diagram show in another.
 */
export function createMermaidCacheKey(code: string, theme: MermaidTheme): string {
  const reversed = Array.from(code).reverse().join("");
  return `${fnv1a32(code).toString(36)}:${fnv1a32(reversed).toString(36)}:${code.length}:${theme}`;
}

let mermaidRenderCounter = 0;

/** Mermaid needs a unique DOM id per render; ids must start with a letter. */
export function nextMermaidRenderId(): string {
  mermaidRenderCounter += 1;
  return `chat-mermaid-${mermaidRenderCounter.toString(36)}`;
}

const MAX_MERMAID_CACHE_ENTRIES = 64;
const MAX_MERMAID_CACHE_MEMORY_BYTES = 8 * 1024 * 1024;

/** Rendered SVG markup by (code, theme), shared across every chat message. */
export const renderedMermaidCache = new LRUCache<string>(
  MAX_MERMAID_CACHE_ENTRIES,
  MAX_MERMAID_CACHE_MEMORY_BYTES,
);

export function estimateMermaidSvgSize(svg: string): number {
  return svg.length * 2;
}

/** Debounce renders while streaming so each new token doesn't re-parse. */
export const MERMAID_STREAMING_RENDER_DELAY_MS = 300;

export function resolveMermaidRenderDelay(isStreaming: boolean): number {
  return isStreaming ? MERMAID_STREAMING_RENDER_DELAY_MS : 0;
}

export type MermaidFigureAlign = "left" | "center" | "right";

export interface PreparedMermaidSvg {
  readonly svg: string;
  /** Width of the diagram's own coordinate space, from its viewBox. */
  readonly naturalWidth: number | null;
}

const ASPECT_ALIGN: Readonly<Record<MermaidFigureAlign, string>> = {
  left: "xMinYMin",
  center: "xMidYMin",
  right: "xMaxYMin",
};

/**
 * Mermaid pins its root svg to the diagram's natural width via an inline
 * `max-width` and centres content with the default preserveAspectRatio.
 * Chat figures fill the column instead (see the CSS), so that pin comes off
 * and the content aligns with the figure's placement.
 */
export function prepareMermaidSvg(svg: string, align: MermaidFigureAlign): PreparedMermaidSvg {
  const openTag = svg.match(/<svg\b[^>]*>/);
  if (!openTag) return { svg, naturalWidth: null };
  const viewBox = openTag[0].match(/viewBox="\s*[-\d.]+\s+[-\d.]+\s+([\d.]+)\s+[\d.]+\s*"/);
  const naturalWidth = viewBox?.[1] ? Number(viewBox[1]) : null;
  let tag = openTag[0]
    .replace(/\s+preserveAspectRatio="[^"]*"/, "")
    .replace(/max-width:\s*[^;"]+;?/, "")
    .replace(/\s+style=""/, "");
  tag = tag.replace(/^<svg\b/, `<svg preserveAspectRatio="${ASPECT_ALIGN[align]} meet"`);
  return {
    svg: svg.replace(openTag[0], tag),
    naturalWidth: naturalWidth != null && Number.isFinite(naturalWidth) ? naturalWidth : null,
  };
}

/** Diagrams may grow to fill the column, but no further than this over natural size. */
export const MERMAID_MAX_UPSCALE = 2;
